import React, { useState, useEffect, useMemo } from 'react'

import { useAppSelector } from 'app-redux/hooks'

import { CardType } from 'types/deck'
import { PRICE_SOURCE, PRICE_COLORS, PRICE_ICON_NAMES } from 'types/active'

import Accordion from 'components/elements/Accordion'
import PhatInput from 'components/formElements/PhatInput'
import CursorCardWrapper from 'components/card/CursorCardWrapper'
import PhatButton from 'components/formElements/PhatButton'
import BuyCardsLink from 'components/formElements/BuyCardsLink'
import SelectMultipleCards from 'components/formElements/SelectMultipleCards'
import BuyFromMultipleVendors from 'components/formElements/BuyCardsFromMultipleVendors'
import Icon from 'components/elements/Icon'

import { getPricesForCards } from 'utils/Price'

import styles from './buyBulkModal.module.scss'

type Props = {
  open?: boolean
  linkedFrom?: string
  preSelectedCards?: CardType[]
  hideHeader?: boolean
  className?: string
}

const BuyBulkModalBody = ({ open, linkedFrom = 'bulkBuy', preSelectedCards, hideHeader, className = '' }: Props) => {
  const [cards, setSelectedCards] = useState<CardType[]>(preSelectedCards || [])
  const [replacedQuantities, setReplacedQuantities] = useState<Record<string, number>>({})
  const isPrivate = useAppSelector(state => state.deck.private)

  useEffect(() => {
    if (!open) return

    setSelectedCards(preSelectedCards?.filter(card => !card.customCard) || [])
  }, [preSelectedCards])

  const { selectedCards, prices } = useMemo(() => {
    const filteredCards = cards.filter(card => !card.customCard)

    return {
      selectedCards: filteredCards.map(c => ({ ...c, qty: replacedQuantities[c.id] || c.qty })),
      prices: getPricesForCards(filteredCards),
    }
  }, [cards, replacedQuantities])

  const handleUpdatedCustomQuantity = (id: string, qty: number) => {
    setReplacedQuantities({ ...replacedQuantities, [id]: qty || 1 })
  }

  const cardTraderTooltip = isPrivate ? 'CardTrader Wishlists are public and your deck is private' : undefined

  return (
    <div className={`${styles.content} ${className}`}>
      {!hideHeader && <h3>Export to retailers</h3>}

      {!preSelectedCards && (
        <SelectMultipleCards onChange={setSelectedCards} className={styles.cardSelector} filterOutCustomCards />
      )}

      <h3>Buy exact printings in deck</h3>
      <div className={styles.outboundLinks}>
        <BuyCardsLink priceSource={PRICE_SOURCE.MP} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['6'] }}>
            <Icon className={PRICE_ICON_NAMES['6']} />
            Mana Pool {prices.mp.totalPrice}
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink priceSource={PRICE_SOURCE.JE} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['9'] }}>
            <Icon className={PRICE_ICON_NAMES['9']} />
            Journey's End ($)
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink isDisabled={isPrivate} priceSource={PRICE_SOURCE.CT} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton
            title={cardTraderTooltip}
            className={styles.retailerButton}
            style={{ backgroundColor: PRICE_COLORS['8'] }}>
            <Icon className={PRICE_ICON_NAMES['8']} />
            CardTrader {prices.cardtrader.totalPrice}
          </PhatButton>
        </BuyCardsLink>
      </div>

      <h3>Buy cheapest printings</h3>
      <div className={styles.outboundLinks}>
        <BuyCardsLink priceSource={PRICE_SOURCE.CK} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['1'] }}>
            <Icon className={PRICE_ICON_NAMES['1']} />
            Card Kingdom {prices.ck.totalMinimumPrice}
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink priceSource={PRICE_SOURCE.TCG} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['2'] }}>
            <Icon className={PRICE_ICON_NAMES['2']} />
            TCG Player {prices.tcg.totalMinimumPrice}
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink priceSource={PRICE_SOURCE.SCG} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['5'] }}>
            <Icon className={PRICE_ICON_NAMES['5']} />
            Star City Games {prices.scg.totalMinimumPrice}
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink priceSource={PRICE_SOURCE.MP} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['6'] }}>
            <Icon className={PRICE_ICON_NAMES['6']} />
            Mana Pool {prices.mp.totalMinimumPrice}
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink priceSource={PRICE_SOURCE.MTGO} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['3'] }}>
            <Icon className={PRICE_ICON_NAMES['3']} />
            Cardhoarder {prices.mtgo.totalMinimumPrice}
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink priceSource={PRICE_SOURCE.TCGLAND} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['7'] }}>
            <Icon className={PRICE_ICON_NAMES['7']} />
            TCG Land {prices.tcgLand.totalMinimumPrice} (MXN)
          </PhatButton>
        </BuyCardsLink>

        <BuyCardsLink isDisabled={isPrivate} priceSource={PRICE_SOURCE.CT} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton
            title={cardTraderTooltip}
            className={styles.retailerButton}
            style={{ backgroundColor: PRICE_COLORS['8'] }}>
            <Icon className={PRICE_ICON_NAMES['8']} />
            CardTrader {prices.cardtrader.totalMinimumPrice}
          </PhatButton>
        </BuyCardsLink>
      </div>

      <h3>Buy from multiple sources</h3>
      <BuyFromMultipleVendors cards={cards} linkedFrom={linkedFrom} />

      <h3>Sell links</h3>
      <div>
        <BuyCardsLink priceSource={PRICE_SOURCE.CK} cards={cards} linkedFrom={`${linkedFrom}_sell`} isSellLink>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['1'] }}>
            <Icon className={PRICE_ICON_NAMES['1']} />
            Sell on Card Kingdom
          </PhatButton>
        </BuyCardsLink>
      </div>

      {!preSelectedCards && (
        <Accordion
          label="Customize quantities"
          containerClass={styles.customQuantities}
          className={styles.customQuantitiesTrigger}>
          {selectedCards.map(card => (
            <div key={card.id} className={styles.customQuantityCard}>
              <PhatInput
                type="number"
                defaultValue={card.qty}
                containerClassName={styles.inputContainer}
                className={styles.input}
                onChange={value => handleUpdatedCustomQuantity(card.id, Number(value))}
                selectOnFocus
              />
              <CursorCardWrapper card={card} className={styles.cardName}>
                {card.name}
              </CursorCardWrapper>
            </div>
          ))}
        </Accordion>
      )}
    </div>
  )
}

export default BuyBulkModalBody
